/**
 * @fileOverview This file defines the `UpdateRegionModal` component, which provides a modal dialog
 * for updating the details of an existing region.
 * @version 1.0.0
 * @since 1.0.0
 * @author Santiago Cano Herrera
 */

//#region Imports
import { useEffect, useState } from 'react';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Skeleton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
//#endregion

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

/**
 * Component for displaying a modal to update an existing region's details.
 * @param {Object} props - The component props.
 * @param {number} props.regionId - The ID of the region to update.
 * @param {boolean} props.showModal - Indicates whether the modal is open.
 * @param {function} props.setShowModal - Function to toggle the modal's visibility.
 * @param {function} props.refreshData - Function to refresh the regions data after updating.
 * @returns {JSX.Element} The `UpdateRegionModal` component.
 */
export const UpdateRegionModal = ({
  regionId,
  showModal,
  setShowModal,
  refreshData,
}) => {
  const [isLoading, setIsLoading] = useState(false); // State for tracking submission process
  const [isFetching, setIsFetching] = useState(false); // State for tracking data fetching
  const [region, setRegion] = useState({ name: '', notes: '' }); // State for region details

  useEffect(() => {
    if (showModal) {
      fetchData(); // Fetch region details when the modal opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Fetches the details of the selected region.
   */
  const fetchData = async () => {
    setIsFetching(true);
    const endpoint = `${GlobalVariables.getURL()}region/${regionId}`;

    try {
      const response = await customAxios.get(endpoint, HEADERS);
      const region = response.data.data;
      setRegion({ name: region.name, notes: region.notes }); // Update region state with fetched details
    } catch (error) {
      console.error('Error fetching region details:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  /**
   * Resets the form fields to their initial state.
   */
  const handleResetFields = () => {
    setRegion({ name: '', notes: '' });
  };

  /**
   * Closes the modal and resets the fields.
   */
  const handleClose = () => {
    setShowModal(false);
    handleResetFields();
    refreshData();
  };

  /**
   * Handles the form submission to update the region's details.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const endpoint = `${GlobalVariables.getURL()}region/update/${regionId}`;

    try {
      const response = await customAxios.patch(endpoint, region, HEADERS);

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Región actualizada exitosamente',
        });
        handleClose(); // Close the modal after successful update
      }
    } catch (error) {
      console.error('Error updating region:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={showModal} maxWidth='md' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Editar región
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <form id='new-region-form' onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {/* Name Field */}
              {isFetching ? (
                <Skeleton variant='rectangular' width='100%' height={56} />
              ) : (
                <TextField
                  fullWidth
                  disabled={isLoading}
                  label='Nombre'
                  placeholder='Nombre...'
                  type='text'
                  value={region.name}
                  onChange={(e) =>
                    setRegion((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Notes Field */}
              {isFetching ? (
                <Skeleton variant='rectangular' width='100%' height={56} />
              ) : (
                <TextField
                  fullWidth
                  disabled={isLoading}
                  label='Notas'
                  placeholder='Notas...'
                  type='text'
                  value={region.notes}
                  onChange={(e) =>
                    setRegion((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                />
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          form='new-region-form'
          type='submit'
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size='2rem' />
          ) : (
            <Typography>Guardar</Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
